import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

class CacheErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    const chunkFailedCSS = /Loading CSS chunk [\d]+ failed/;
    
    if (chunkFailedMessage.test(error.message) || chunkFailedCSS.test(error.message)) {
      return { hasError: true, isChunkError: true };
    }
    
    return { hasError: true, isChunkError: false };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      }).then(() => {
        window.location.reload(true);
      });
    } else {
      window.location.reload(true);
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // This is likely a cache issue
        return (
          <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <RefreshIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Update Available
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                A new version of the portal is available. Please click the button below to update.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={this.handleReload}
                startIcon={<RefreshIcon />}
                size="large"
              >
                Update Now
              </Button>
            </Paper>
          </Container>
        );
      }

      // Some other error
      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="error">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We're sorry, but something went wrong. Please try refreshing the page.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
              startIcon={<RefreshIcon />}
            >
              Refresh Page
            </Button>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default CacheErrorBoundary;